import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Calendar, DollarSign } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StrategySessionForm from '../components/StrategySessionForm';
import BookingModal from '../components/BookingModal';
import usePageMeta from '../hooks/usePageMeta';

const StrategySession = () => {
  usePageMeta({
    title: 'Free Strategy Call | AI Office Manager for HVAC, Plumbing & Electrical Contractors',
    description:
      "Upload your call logs and we'll show you exactly how much revenue you're leaving on voicemail — then we'll demo your AI Office Manager answering calls in your voice, live on a free 60-minute strategy call.",
  });
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const included = [
    'Live call-log audit: how many jobs you missed last 30 days',
    'Missed-revenue calculation based on your avg job value',
    'Walk-through of your AI Office Manager answering live',
    'Custom voice + script built around your trade and service area',
    'CRM/dispatch integration plan (ServiceTitan, HCP, Jobber, etc.)',
    'After-hours + overflow routing strategy',
    'Go-live plan: number porting, testing, and team handoff'
  ];

  const process = [
    {
      step: '01',
      title: 'Pre-Call Audit',
      description: 'Send us your call logs from the last 30 days. We map missed calls, after-hours volume, and estimated lost revenue before we even get on the call.'
    },
    {
      step: '02',
      title: 'Live 60-Minute Strategy Call',
      description: 'We show you your AI Office Manager answering test calls in your voice, review the missed-revenue audit, and map exactly how it plugs into your current dispatch flow.'
    },
    {
      step: '03',
      title: 'Go-Live Roadmap',
      description: 'Within 48 hours you get a written plan: setup timeline, integration steps, expected recovered revenue, and your custom quote.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header onOpenQuiz={() => {}} />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-[#050505] to-[#0F0F13]">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/" className="inline-flex items-center gap-2 text-[#00E599] hover:text-[#00C985] font-body text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 bg-[#00E599]/10 border border-[#00E599]/20 rounded-full text-[#00E599] text-sm font-accent mb-6">
              Free 60-Minute AI Office Manager Strategy Call
            </span>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-headline font-bold text-[#F4F4F5] leading-tight tracking-tighter mb-6">
              See Exactly How Much Revenue
              <span className="block mt-2 gradient-text">You're Leaving on Voicemail</span>
            </h1>

            <p className="text-lg sm:text-xl text-[#A1A1AA] font-body leading-relaxed mb-8">
              Send us your call logs. We'll run a missed-revenue audit, show you your AI Office Manager answering calls in your voice, and hand you a step-by-step go-live plan. No pitch deck, no pressure.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => setIsFormOpen(true)}
                size="lg"
                className="bg-[#00E599] hover:bg-[#00C985] text-[#050505] whitespace-nowrap w-full sm:w-auto px-5 sm:px-8 py-4 text-sm sm:text-base font-body font-bold transition-all duration-300 hover:shadow-xl hover:shadow-[#00E599]/30 rounded-lg"
              >
                Book My Free Strategy Call
              </Button>
              <a
                href="#book"
                onClick={(e) => {
                  e.preventDefault();
                  setIsBookingOpen(true);
                }}
                data-testid="strategy-view-calendar-cta"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap w-full sm:w-auto px-5 sm:px-8 py-4 glass rounded-lg text-sm sm:text-base text-[#F4F4F5] font-body font-semibold hover:border-[#00E599]/30 transition-all cursor-pointer"
              >
                <Calendar className="w-5 h-5 shrink-0" />
                View Calendar
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-headline font-bold text-[#F4F4F5] mb-4">
              What's Included in Your Strategy Call
            </h2>
            <p className="text-lg text-[#A1A1AA] font-body max-w-2xl mx-auto">
              This isn't a sales call. It's a working session built for contractors — you leave with a hard number on what you're losing and a clear plan to recover it.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-4">
            {included.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="flex items-start gap-3 glass p-4 rounded-lg"
              >
                <Check className="w-6 h-6 text-[#00E599] flex-shrink-0 mt-0.5" />
                <span className="text-[#F4F4F5] font-body">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Process */}
      <section className="py-20 bg-gradient-to-b from-[#050505] to-[#0F0F13]">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-headline font-bold text-[#F4F4F5] mb-4">
              How It Works
            </h2>
            <p className="text-lg text-[#A1A1AA] font-body max-w-2xl mx-auto">
              From booking to roadmap delivery, the entire process takes less than one week.
            </p>
          </motion.div>

          <div className="space-y-8">
            {process.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex gap-6"
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00E599] to-[#00D4FF] flex items-center justify-center">
                    <span className="text-xl font-headline font-bold text-[#050505]">{item.step}</span>
                  </div>
                </div>
                <div className="flex-1 glass p-6 rounded-xl">
                  <h3 className="text-xl font-headline font-bold text-[#F4F4F5] mb-2">{item.title}</h3>
                  <p className="text-[#A1A1AA] font-body">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass p-10 rounded-2xl text-center"
          >
            <DollarSign className="w-12 h-12 text-[#00E599] mx-auto mb-4" />
            <h2 className="text-3xl font-headline font-bold text-[#F4F4F5] mb-4">
              Cost
            </h2>
            <div className="mb-4">
              <span className="text-5xl font-headline font-bold gradient-text">$0</span>
            </div>
            <p className="text-[#00E599] font-body font-semibold mb-6">
              Free for qualifying HVAC, Plumbing, and Electrical contractors
            </p>
            <p className="text-[#A1A1AA] font-body mb-8">
              We run the missed-revenue audit and build your AI Office Manager demo on our dime. If it doesn't make sense for your shop, no worries — you walk away with the report either way.
            </p>
            <Button
              onClick={() => setIsFormOpen(true)}
              size="lg"
              className="bg-[#00E599] hover:bg-[#00C985] text-[#050505] whitespace-nowrap w-full sm:w-auto px-5 sm:px-8 py-4 text-sm sm:text-base font-body font-bold transition-all duration-300 hover:shadow-xl hover:shadow-[#00E599]/30 rounded-lg"
            >
              Book My Free Strategy Call
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-20 bg-gradient-to-b from-[#050505] to-[#0F0F13]">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-headline font-bold text-[#F4F4F5] mb-4">
              This Call Is For You If...
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-6">
            {[
              'You run an HVAC, Plumbing, or Electrical shop doing $500K+ per year',
              'Calls ring out after 5pm and on weekends',
              'Your CSR or answering service is missing leads',
              'You\'re paying for Google Ads and losing leads to voicemail',
              'You want to add capacity without hiring another body',
              'You\'re tired of callbacks that never convert'
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="glass p-6 rounded-xl border-l-4 border-[#00E599]"
              >
                <p className="text-[#F4F4F5] font-body">{item}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#050505]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-headline font-bold text-[#F4F4F5] mb-6">
              Ready to See What You're Leaving on Voicemail?
            </h2>
            <p className="text-lg text-[#A1A1AA] font-body mb-8">
              Book your free strategy call. We'll have the missed-revenue audit ready before we even get on the phone.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => setIsFormOpen(true)}
                size="lg"
                className="bg-[#00E599] hover:bg-[#00C985] text-[#050505] whitespace-nowrap w-full sm:w-auto px-5 sm:px-8 py-4 text-sm sm:text-base font-body font-bold transition-all duration-300 hover:shadow-xl hover:shadow-[#00E599]/30 rounded-lg"
              >
                Book My Free Strategy Call
              </Button>
              <a
                href="#book"
                onClick={(e) => {
                  e.preventDefault();
                  setIsBookingOpen(true);
                }}
                data-testid="strategy-view-calendar-cta"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap w-full sm:w-auto px-5 sm:px-8 py-4 glass rounded-lg text-sm sm:text-base text-[#F4F4F5] font-body font-semibold hover:border-[#00E599]/30 transition-all cursor-pointer"
              >
                <Calendar className="w-5 h-5 shrink-0" />
                View Calendar
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <StrategySessionForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
      <BookingModal isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </div>
  );
};

export default StrategySession;
